import { forwardRef, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

/** Hairline row separator for <tbody> rows. <Th> already applies it to header
 * cells, so header <tr>s must not re-apply it (it would apply 2–3× over). */
export const TABLE_ROW_BORDER_CLASSES = 'border-b border-hairline';

/**
 * Highlight for the viewer's own row — `AllianceMemberTable` and
 * `AllianceBrowseList` both used a duplicated `bg-emerald-500/10`; this is the
 * single source of truth built on `bg-status-success/10`.
 */
export const TABLE_ROW_HIGHLIGHT_CLASSES = 'bg-status-success/10';

/**
 * Sticky-header recipe for <th>. The background must be fully opaque — the
 * tables that pin their header render inside solid `bg-space-800` panels, so a
 * translucent class would smear rows underneath. And the bottom edge can NOT
 * come from `border-b`: `border-collapse: collapse` (Tailwind's preflight)
 * drops stickied cells' borders entirely, so the hairline underline is a
 * `box-shadow` painted on the cell's own box instead (the `hairline` token's
 * literal value matters here — it can't be a named color class).
 * Combine with the `sticky` prop on <Th> and `stickyHeader` on <Table>.
 */
export const TABLE_HEADER_STICKY_CLASSES = 'sticky top-0 z-10 bg-space-800 shadow-[0_1px_0_0_rgba(230,232,245,0.10)]';

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
    /**
     * Turn the wrapper into a bounded vertical scroll region so `sticky`
     * headers stay visible over a long table (e.g. alliance rosters). The
     * table keeps its horizontal overflow behavior on narrow screens.
     */
    stickyHeader?: boolean;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(function Table(
    { className, children, stickyHeader = false, ...rest },
    ref,
) {
    return (
        <div className={cn('overflow-x-auto', stickyHeader && 'overflow-y-auto max-h-[32rem]')}>
            <table ref={ref} className={cn('w-full text-body text-left', className)} {...rest}>
                {children}
            </table>
        </div>
    );
});

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
    /** Screen-reader-only header (e.g. "Actions" — needed for a11y, not visible). */
    srOnly?: boolean;
    /** Pin the header to its scroll region's top edge (combine with `<Table stickyHeader>`). */
    sticky?: boolean;
    /** Mirrors `Td`'s prop so `<Th align="right">` lands as `text-right` instead of a raw HTML attr. */
    align?: 'left' | 'right';
}

export const Th = forwardRef<HTMLTableCellElement, ThProps>(function Th(
    { srOnly = false, sticky = false, align = 'left', className, children, ...rest },
    ref,
) {
    return (
        <th
            ref={ref}
            scope="col"
            className={cn(
                'px-3 py-2 text-label text-content-muted font-medium',
                align === 'right' && 'text-right',
                srOnly && 'sr-only',
                TABLE_ROW_BORDER_CLASSES,
                // `sr-only` sets `position: absolute`; never stack `sticky`
                // (position: sticky) on top of it — both would land in the
                // class list and leave the winner up to stylesheet order.
                sticky && !srOnly && TABLE_HEADER_STICKY_CLASSES,
                className,
            )}
            {...rest}
        >
            {children}
        </th>
    );
});

export interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
    align?: 'left' | 'right';
}

export const Td = forwardRef<HTMLTableCellElement, TdProps>(function Td(
    { align = 'left', className, children, ...rest },
    ref,
) {
    return (
        <td
            ref={ref}
            className={cn('px-3 py-2 text-body', align === 'right' && 'text-right', className)}
            {...rest}
        >
            {children}
        </td>
    );
});

export default Table;
